import { useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { getMetaData } from '../../../tool';
import { Comment, DatabaseComment } from '../../../types/types';
import useUserContext from '../../../hooks/useUserContext';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Card, CardContent } from '../../ui/card';
import cn from '../../../lib/utils';

/**
 * Interface representing the props for the Comment Section component.
 *
 * - comments - list of the comment components
 * - handleAddComment - a function that handles adding a new comment, taking a Comment object as an argument
 */
interface CommentSectionProps {
  comments: DatabaseComment[];
  handleAddComment: (comment: Comment) => void;
}

/**
 * CommentSection component shows the users all the comments and allows the users add more comments.
 *
 * @param comments: an array of Comment objects
 * @param handleAddComment: function to handle the addition of a new comment
 */
const CommentSection = ({ comments, handleAddComment }: CommentSectionProps) => {
  const { user } = useUserContext();
  const [text, setText] = useState<string>('');
  const [textErr, setTextErr] = useState<string>('');
  const [showComments, setShowComments] = useState<boolean>(false);

  /**
   * Function to handle the addition of a new comment.
   */
  const handleAddCommentClick = () => {
    if (text.trim() === '' || user.username.trim() === '') {
      setTextErr(text.trim() === '' ? 'Comment text cannot be empty' : '');
      return;
    }

    const newComment: Comment = {
      text,
      commentBy: user.username,
      commentDateTime: new Date(),
    };

    handleAddComment(newComment);
    setText('');
    setTextErr('');
  };

  return (
    <>
      <Button variant='outline' className='' onClick={() => setShowComments(!showComments)}>
        {showComments ? 'Hide Comments' : 'Show Comments'}
      </Button>
      {showComments && (
        <Card className='mb-4 bg-gray-50'>
          <CardContent className='pt-4'>
            {showComments && (
              <div className='mt-3'>
                <ul className='space-y-2 mb-4'>
                  {comments.length > 0 ? (
                    comments.map(comment => (
                      <li
                        key={String(comment._id)}
                        className='p-2 bg-white border border-gray-200 rounded-md'>
                        <div className='text-sm mb-1 prose prose-sm max-w-none'>
                          <ReactMarkdown>{comment.text}</ReactMarkdown>
                        </div>
                        <span className='text-xs text-gray-500'>
                          <Link
                            to={`/user/${comment.commentBy}`}
                            className='text-blue-600 hover:underline cursor-pointer'>
                            {comment.commentBy}
                          </Link>
                          , {getMetaData(new Date(comment.commentDateTime))}
                        </span>
                      </li>
                    ))
                  ) : (
                    <p className='text-sm italic text-gray-500'>No comments yet.</p>
                  )}
                </ul>

                <div className='space-y-2'>
                  <div className='flex gap-6 items-center'>
                    <Textarea
                      placeholder='Add a comment...'
                      value={text}
                      onChange={e => setText(e.target.value)}
                      className={cn(
                        'flex-1 text-sm min-h-[60px]',
                        textErr && 'border-red-500 focus-visible:ring-red-500',
                      )}
                    />
                    <Button
                      variant='default'
                      size='lg'
                      className='bg-green-600 hover:bg-green-700 text-white'
                      onClick={handleAddCommentClick}>
                      Add Comment
                    </Button>
                  </div>
                  {textErr && <p className='text-xs text-red-500'>{textErr}</p>}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default CommentSection;
